import { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  IconButton,
  SwipeableDrawer,
  Checkbox,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import api from "../../utils/api";
import FileIcon from "@mui/icons-material/FileUpload";
import AddPhoto from "./../../assets/add-photo.png";
import BlueButton from "../../components/BlueButton/BlueButton";
import { toast } from "react-toastify";

const CreateEditShop = ({
  open,
  onClose,
  initialData = null, // Pass shop data for edit mode
  onSuccess, // Callback after successful submit
  type = "shop",
}) => {
  // Form data state
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    whatsapp: "",
    email: "",
    url: "",
    app_percentage: "",
    provider_name: "",
    account_no: "",
    wallet_number: "",
    password: "",
    password_confirmation: "",
    address: "",
    description: "",
    allow_negotiation: false,
    availability_samples: false,
  });

  // Location states
  const [countries, setCountries] = useState([]);
  const [governorates, setGovernorates] = useState([]);
  const [cities, setCities] = useState([]);

  const [country, setCountry] = useState("");
  const [province, setProvince] = useState(1);
  const [city, setCity] = useState(1);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [workingScope, setWorkingScope] = useState("");

  // UI states
  const [showPassword, setShowPassword] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Bank"); // bank or wallet
  const [loading, setLoading] = useState(false);

  // File states
  const [logo, setLogo] = useState(null);
  const [idFrontImage, setIdFrontImage] = useState(null);
  const [idBackImage, setIdBackImage] = useState(null);

  const [idFrontPreview, setIdFrontPreview] = useState(null);
  const [idBackPreview, setIdBackPreview] = useState(null);

  const [logoPreview, setLogoPreview] = useState(null);

  // Basic form field initialization
  useEffect(() => {
    if (initialData) {
      // Set basic form data
      setFormData({
        name: initialData.name || "",
        phone: initialData.phone || "",
        whatsapp: initialData.whatsapp || "",
        email: initialData.email || "",
        url: initialData.url || "",
        app_percentage: initialData.app_percentage?.toString() || "",
        provider_name: initialData.provider_name || "",
        account_no: initialData.account_no || "",
        wallet_number: initialData.wallet_number || "",
        address: initialData.address || "",
        description: initialData.description || "",
        allow_negotiation: initialData.allow_negotiation,
        availability_samples: initialData.availability_samples,
      });

      // Set payment method
      setPaymentMethod(
        initialData.acc_type?.toLowerCase() === ("bank" || "Bank")
          ? "Bank"
          : "wallet"
      );

      // Handle previews for existing images
      if (initialData.logo) {
        setLogoPreview(initialData.logo);
      }

      if (initialData.id_front_image) {
        setIdFrontPreview(initialData.id_front_image);
      }

      if (initialData.id_back_image) {
        setIdBackPreview(initialData.id_back_image);
      }
    }
  }, [initialData]);

  // Fetch countries and find matching country by name
  useEffect(() => {
    const getCountries = async () => {
      try {
        const res = await api.get("/countries");
        setCountries(res.data.data);

        // If we have initialData with a country name, find the matching country ID
        if (initialData && initialData.country && res.data.data.length > 0) {
          const matchingCountry = res.data.data.find(
            (c) => c.name_ar === initialData.country
          );

          if (matchingCountry) {
            setCountry(matchingCountry.id);
          } else {
          }
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    getCountries();
  }, [initialData]);

  // Fetch governorates when country changes and find matching by name
  useEffect(() => {
    const getGovernorates = async () => {
      if (!country) {
        setGovernorates([]);
        return;
      }

      try {
        const params = new URLSearchParams({
          country_id: country,
        });
        const res = await api.get(`/Governorates?${params.toString()}`);
        setGovernorates(res.data.data);

        // If we have initialData with a governorate name, find the matching governorate ID
        if (
          initialData &&
          initialData.governorate &&
          res.data.data.length > 0
        ) {
          const matchingGov = res.data.data.find(
            (g) => g.name_ar === initialData.governorate
          );

          if (matchingGov) {
            setProvince(matchingGov.id);
          } else {
          }
        }
      } catch (error) {
        console.error("Error fetching governorates:", error);
      }
    };

    if (country) {
      getGovernorates();
    }
  }, [country, initialData]);

  // Fetch cities when province changes and find matching by name
  useEffect(() => {
    const getCities = async () => {
      if (!province) {
        setCities([]);
        return;
      }

      try {
        const params = new URLSearchParams({
          governorate_id: province,
        });
        const res = await api.get(`/cities?${params.toString()}`);
        setCities(res.data.data);

        // If we have initialData with a city name, find the matching city ID
        if (initialData && initialData.city && res.data.data.length > 0) {
          const matchingCity = res.data.data.find(
            (c) => c.name_ar === initialData.city
          );

          if (matchingCity) {
            setCity(matchingCity.id);
          } else {
          }
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };

    if (province) {
      getCities();
    }
  }, [province, initialData]);

  // Fetch banks and match bank name with ID
  useEffect(() => {
    const getBanks = async () => {
      if (!country) {
        setBanks([]);
        return;
      }

      try {
        const params = new URLSearchParams({
          country_id: country,
        });
        const res = await api.get(`/banks?${params.toString()}`);
        setBanks(res.data.data);

        // If we have initialData with a bank name, find the matching bank ID
        if (initialData && initialData.bank_name && res.data.data.length > 0) {
          const matchingBank = res.data.data.find(
            (b) => b.name_ar === initialData.bank_name
          );

          if (matchingBank) {
            setSelectedBank(matchingBank.id);
          } else {
          }
        }
      } catch (error) {
        console.error("Error fetching banks:", error);
      }
    };

    if (country) {
      getBanks();
    }
  }, [country, initialData]);

  // Set working scope ID based on name
  useEffect(() => {
    // If we have countries loaded and initialData with working_scope
    if (countries.length > 0 && initialData && initialData.working_scope) {
      const matchingScope = countries.find(
        (c) => c.name === initialData.working_scope
      );

      if (matchingScope) {
        setWorkingScope(matchingScope.id);
      } else {
      }
    }
  }, [countries, initialData]);
  // Input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Update the change handlers for ID images
  const handleIdFrontChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIdFrontImage(file);
      setIdFrontPreview(URL.createObjectURL(file));
    }
  };

  const handleIdBackChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIdBackImage(file);
      setIdBackPreview(URL.createObjectURL(file));
    }
  };

  // Logo change handler
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  // Add remove handlers
  const removeIdFront = () => {
    setIdFrontImage(null);
    setIdFrontPreview(null);
    // Clear the file input
    document.getElementById("id_front").value = "";
  };

  const removeIdBack = () => {
    setIdBackImage(null);
    setIdBackPreview(null);
    // Clear the file input
    document.getElementById("id_back").value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Always use FormData for all requests
      const formDataToSend = new FormData();

      // Append form data
      Object.keys(formData).forEach((key) => {
        // Skip password confirmation field completely
        if (key === "password_confirmation") {
          return;
        }

        // Skip password fields if editing
        if (initialData && key === "password") {
          return;
        }

        // Handle boolean fields
        if (key === "allow_negotiation" || key === "availability_samples") {
          formDataToSend.append(key, formData[key] ? 1 : 0);
        }
        // Include all values including 0
        else if (formData[key] !== undefined && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add location data
      formDataToSend.append("country_id", country || "");
      formDataToSend.append("governorate_id", province || "");
      formDataToSend.append("city_id", city || "");
      formDataToSend.append("type", type);
      formDataToSend.append("working_scope", workingScope || "");

      // For factory type, add boolean fields
      if (type === "factory") {
        formDataToSend.append(
          "allow_negotiation",
          formData.allow_negotiation ? 1 : 0
        );
        formDataToSend.append(
          "availability_samples",
          formData.availability_samples ? 1 : 0
        );
      }

      // Append files (if they exist)
      if (logo instanceof File) formDataToSend.append("logo", logo);
      if (idFrontImage instanceof File)
        formDataToSend.append("id_front_image", idFrontImage);
      if (idBackImage instanceof File)
        formDataToSend.append("id_back_image", idBackImage);

      // Add payment type
      formDataToSend.append("acc_type", paymentMethod);
      if (paymentMethod === "Bank") {
        formDataToSend.append("bank_id", selectedBank || "");
      }

      // If updating, add the ID to the FormData
      if (initialData) {
        formDataToSend.append("id", initialData.id);
      }

      if (initialData) {
        // Update existing vendor
        await api.post(`/update-vendor/${initialData.id}`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        // Create new vendor
        if (!logo) {
          toast.error("يرجى إضافة صورة الشعار");
          setLoading(false);
          return;
        }
        await api.post("/vendors", formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response.data.message || "حدث خطأ أثناء إضافة المتجر");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      phone: "",
      whatsapp: "",
      email: "",
      url: "",
      app_percentage: "",
      provider_name: "",
      account_no: "",
      wallet_number: "",
      password: "",
      password_confirmation: "",
      address: "",
      description: "",
      allow_negotiation: false,
      availability_samples: false,
    });
    setLogo(null);
    setIdFrontImage(null);
    setIdBackImage(null);
    setCountry("");
    setProvince("");
    setCity("");
    setPaymentMethod("bank");
    setIdFrontPreview(null);
    setIdBackPreview(null);
    setLogoPreview(null);
    onClose();
  };

  return (
    <SwipeableDrawer
      onClose={() => {
        onClose();
        handleClose();
      }}
      onOpen={() => {}}
      anchor="left"
      open={open}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      <div className="user-modal">
        <p className="user-modal-p">
          <p className="user-modal-p">
            {initialData
              ? `تعديل ال${type === "factory" ? "مصنع" : "متجر"}`
              : `اضافة ${type === "factory" ? "مصنع" : "متجر"} جديد`}
          </p>
        </p>
        <form onSubmit={handleSubmit}>
          {/* Logo Upload */}
          <div className="form-img">
            <label htmlFor="logo" className="img-control">
              <img
                src={
                  logo instanceof File
                    ? URL.createObjectURL(logo)
                    : logoPreview || AddPhoto
                }
                alt=""
              />
              <span>اضافة صورة الشعار</span>
            </label>
            <input type="file" id="logo" onChange={handleLogoChange} />
          </div>

          {/* Name Field */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="name"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              {type === "shop" ? " اسم المتجر" : " اسم المصنع"}
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              type="text"
              notched
            />
          </FormControl>

          {/* Phone Number */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="phone"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رقم الموبايل
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="phone"
              name="phone"
              type="text"
              value={formData.phone}
              onChange={handleInputChange}
              notched
            />
          </FormControl>

          {/* WhatsApp Number */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="whatsapp"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رقم الواتس اب
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="whatsapp"
              name="whatsapp"
              type="text"
              value={formData.whatsapp}
              onChange={handleInputChange}
              notched
            />
          </FormControl>

          {/* Email */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="email"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              البريد الالكتروني
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              notched
            />
          </FormControl>

          {/* Country Select */}
          <FormControl fullWidth>
            <InputLabel
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              البلد
            </InputLabel>
            <Select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              displayEmpty
              sx={{ borderRadius: "8px" }}
            >
              <MenuItem value="">اختر البلد</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {country.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Governorate Select */}
          <FormControl fullWidth disabled={!country}>
            <InputLabel
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              المحافظة
            </InputLabel>
            <Select
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              displayEmpty
              sx={{ borderRadius: "8px" }}
            >
              <MenuItem value="">اختر المحافظة</MenuItem>
              {governorates.map((gov) => (
                <MenuItem key={gov.id} value={gov.id}>
                  {gov.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* City Select */}
          <FormControl fullWidth disabled={!province}>
            <InputLabel
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              المدينة
            </InputLabel>
            <Select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              displayEmpty
              sx={{ borderRadius: "8px" }}
            >
              <MenuItem value="">اختر المدينة</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name_ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Address Field */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="address"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              العنوان
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="address"
              name="address"
              type="text"
              value={formData.address || ""}
              onChange={handleInputChange}
              notched
            />
          </FormControl>

          {/* Description Field */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="description"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              الوصف
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="description"
              name="description"
              multiline
              rows={4}
              type="text"
              value={formData.description || ""}
              onChange={handleInputChange}
              notched
            />
          </FormControl>

          {/* Store URL */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="url"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رابط المتجر
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              type="text"
              notched
              placeholder="معرف فريد خاص بك علي سبيل المثال Flower-Store"
            />
          </FormControl>

          {/* App Percentage */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="app_percentage"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              نسبة التطبيق
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="app_percentage"
              name="app_percentage"
              value={formData.app_percentage}
              onChange={handleInputChange}
              type="text"
              notched
              endAdornment={<InputAdornment position="end">%</InputAdornment>}
            />
          </FormControl>

          {type === "factory" && (
            <FormControl
              sx={{
                width: "100%",
                border: "1px solid #DBDEE1",
                borderRadius: "8px",
                padding: 2,
              }}
            >
              <FormLabel>خيارات المصنع</FormLabel>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.allow_negotiation}
                      onChange={(e) =>
                        handleInputChange({
                          target: {
                            name: "allow_negotiation",
                            value: e.target.checked,
                          },
                        })
                      }
                      color="success"
                    />
                  }
                  label="التواصل مع العملاء"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.availability_samples}
                      onChange={(e) =>
                        handleInputChange({
                          target: {
                            name: "availability_samples",
                            value: e.target.checked,
                          },
                        })
                      }
                      color="success"
                    />
                  }
                  label="إضافة عينات"
                />
              </div>
            </FormControl>
          )}

          {/* Payment Method Radio */}
          <FormControl
            sx={{
              width: "100%",
              border: "1px solid #DBDEE1",
              borderRadius: "8px",
              padding: 2,
            }}
          >
            <FormLabel id="payment-method-label">
              طريقة استلام المستحقات
            </FormLabel>
            <RadioGroup
              row
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              sx={{ width: "100%", justifyContent: "flex-start" }}
            >
              <FormControlLabel
                value="Bank"
                control={<Radio color="success" />}
                label="البطاقة الائتمانية"
                sx={{ margin: 0 }}
              />
              <FormControlLabel
                value="wallet"
                control={<Radio color="success" />}
                label="المحفظة الالكترونية"
                sx={{ margin: 0 }}
              />
            </RadioGroup>
          </FormControl>

          {/* Conditional Bank/Wallet Fields */}
          {paymentMethod === "Bank" ? (
            <>
              {/* Working Scope Selection */}
              <FormControl fullWidth>
                <InputLabel
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  نطاق العمل
                </InputLabel>
                <Select
                  value={workingScope}
                  onChange={(e) => setWorkingScope(e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: "8px" }}
                >
                  <MenuItem value="">اختر نطاق العمل</MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.name_ar}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bank Selection */}
              <FormControl fullWidth>
                <InputLabel
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  اختر البنك
                </InputLabel>
                <Select
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: "8px" }}
                >
                  <MenuItem value="">اختر البنك</MenuItem>
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.name_ar}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ width: "100%" }} variant="outlined">
                <InputLabel
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  اسم صاحب الحساب
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  required
                  name="provider_name"
                  value={formData.provider_name}
                  onChange={handleInputChange}
                  type="text"
                  notched
                />
              </FormControl>

              <FormControl sx={{ width: "100%" }} variant="outlined">
                <InputLabel
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  رقم الحساب
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  required
                  name="account_no"
                  value={formData.account_no}
                  onChange={handleInputChange}
                  type="text"
                  inputProps={{
                    pattern: "[0-9]*", // Only numbers allowed
                    minLength: "10", // Minimum length
                    maxLength: "20", // Maximum length
                  }}
                  notched
                />
              </FormControl>
            </>
          ) : (
            <FormControl sx={{ width: "100%" }} variant="outlined">
              <InputLabel
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                رقم المحفظة
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                name="wallet_number"
                value={formData.wallet_number}
                onChange={handleInputChange}
                type="text"
                notched
              />
            </FormControl>
          )}

          {/* ID Images Upload */}
          <div
            className="files-section"
            style={{ width: "100%", textAlign: "center" }}
          >
            <div className="files-control">
              <label htmlFor="id_front" className="files-label">
                <span>صورة البطاقة (الوجه الأمامي)</span>
                <FileIcon className="icon" />
              </label>
              <input type="file" id="id_front" onChange={handleIdFrontChange} />
            </div>

            {/* Preview for front ID */}
            {idFrontPreview && (
              <div className="image-preview">
                <img
                  src={idFrontPreview}
                  alt="Front ID Preview"
                  style={{
                    maxWidth: "80%",
                    maxHeight: "150px",
                    marginTop: "8px",
                  }}
                />
                <button
                  type="button"
                  onClick={removeIdFront}
                  style={{
                    marginTop: "8px",
                    background: "#f44336",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    padding: "5px 10px",
                    cursor: "pointer",
                  }}
                >
                  إزالة
                </button>
              </div>
            )}
          </div>

          <div
            className="files-section"
            style={{ width: "100%", textAlign: "center" }}
          >
            <div className="files-control">
              <label htmlFor="id_back" className="files-label">
                <span>صورة البطاقة (الوجه الخلفي)</span>
                <FileIcon className="icon" />
              </label>
              <input type="file" id="id_back" onChange={handleIdBackChange} />
            </div>

            {/* Preview for back ID */}
            {idBackPreview && (
              <div className="image-preview">
                <img
                  src={idBackPreview}
                  alt="Back ID Preview"
                  style={{
                    maxWidth: "80%",
                    maxHeight: "150px",
                    marginTop: "8px",
                  }}
                />
                <button
                  type="button"
                  onClick={removeIdBack}
                  style={{
                    marginTop: "8px",
                    background: "#f44336",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    padding: "5px 10px",
                    cursor: "pointer",
                  }}
                >
                  إزالة
                </button>
              </div>
            )}
          </div>

          {/* Only show password fields for new shops */}
          {!initialData && (
            <>
              <FormControl sx={{ width: "100%" }} variant="outlined">
                <InputLabel
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  كلمة المرور
                </InputLabel>
                <OutlinedInput
                  required
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleInputChange}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>

              <FormControl sx={{ width: "100%" }} variant="outlined">
                <InputLabel
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  تأكيد كلمة المرور
                </InputLabel>
                <OutlinedInput
                  required
                  name="password_confirmation"
                  type={showPassword ? "text" : "password"}
                  value={formData.password_confirmation}
                  onChange={handleInputChange}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </>
          )}

          <BlueButton
            text={initialData ? "حفظ التغييرات" : "اضافة"}
            size="medium"
            variant="fill"
            type="submit"
            loading={loading}
            disabled={loading}
          />
        </form>
      </div>
    </SwipeableDrawer>
  );
};

export default CreateEditShop;
